import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { SignedIn, SignedOut } from '@clerk/clerk-react'
import { Analytics } from '@/analytics/events'
import { HeroCTA } from '../components/HeroCTA'
import { FeatureBadge } from '@/components/FeatureBadge'
import { SEOTags } from '@/components/SEOTags'
import { WaitlistModal } from '@/components/Modals/WaitlistModal'

export default function HomePage() {
  const homeSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://steadfasthsa.com/#organization",
        "name": "Steadfast HSA",
        "url": "https://steadfasthsa.com",
        "logo": "https://steadfasthsa.com/logo.png"
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://steadfasthsa.com/#software",
        "name": "Steadfast HSA",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "WebBrowser",
        "url": "https://steadfasthsa.com",
        "offers": {
          "@type": "Offer",
          "price": "39.00",
          "priceCurrency": "USD"
        }
      }
    ]
  });

  const location = useLocation();
  const navigate = useNavigate();
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);

  useEffect(() => {
    Analytics.landingPageVisit()
  }, [])

  useEffect(() => {
    if (location.hash === '#waitlist') {
      setIsWaitlistOpen(true);
    }
  }, [location.hash]);

  const handleCloseWaitlist = () => {
    setIsWaitlistOpen(false);
    if (location.hash === '#waitlist') {
      navigate('/', { replace: true });
    }
  };

  return (
    <div>
      <WaitlistModal isOpen={isWaitlistOpen} onClose={handleCloseWaitlist} />
      <SEOTags
        title="Steadfast HSA | The Receipt Vault for Health Savings Accounts"
        description="Your HSA documentation should outlast your custodian. Capture expenses now, export everything later. Purpose-built receipt tracking for the stealth IRA strategy."
        url="https://steadfasthsa.com"
        schema={homeSchema}
      />
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="text-2xl font-bold tracking-tight text-navy">
            STEADFAST <span className="text-gold">HSA</span>
          </div>
          <div className="hidden md:flex space-x-8 text-sm font-semibold text-slate-600">
            <a href="#problem" className="hover:text-navy transition">The Problem</a>
            <a href="#features" className="hover:text-navy transition">Features</a>
            <a href="#trust" className="hover:text-navy transition">Why Us</a>
            <a href="#pricing" className="hover:text-navy transition">Pricing</a>
            <a href="#faq" className="hover:text-navy transition">FAQ</a>
            <Link to="/blog" className="hover:text-navy transition">Blog</Link>
          </div>
          <SignedOut>
            <a href="#waitlist" className="bg-navy text-white px-5 py-2 rounded-md text-sm font-bold hover:opacity-90 transition">
              Join The Waitlist
            </a>
          </SignedOut>
          <SignedIn>
            <Link
              to="/dashboard"
              className="bg-navy text-white px-5 py-2 rounded-md text-sm font-bold hover:opacity-90 transition"
            >
              Dashboard
            </Link>
          </SignedIn>
        </div>
      </nav>

      {/* Hero */}
      <header className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-navy leading-tight mb-6">
            The Receipt Vault for People Who Think in Decades.
          </h1>
          <p className="text-xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Your HSA documentation should outlast your custodian. Capture expenses now,
            export everything later—whether that's 5 years or 30. No lock-in, ever.
          </p>

          <SignedOut>
            <HeroCTA />
          </SignedOut>
          <SignedIn>
            <Link
              to="/dashboard"
              className="inline-block bg-navy text-white px-8 py-4 rounded-lg text-lg font-bold shadow-lg hover:translate-y-[-2px] transition"
            >
              Go to Dashboard
            </Link>
          </SignedIn>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap justify-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <svg aria-hidden="true" className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Full data export
            </span>
            <span className="flex items-center gap-2">
              <svg aria-hidden="true" className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No platform lock-in
            </span>
            <span className="flex items-center gap-2">
              <svg aria-hidden="true" className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Bootstrapped &amp; profitable
            </span>
          </div>
        </div>
      </header>

      {/* Problem Section */}
      <section id="problem" className="py-20 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-navy mb-6">
                Custodians focus on spending. We focus on documentation that lasts.
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                You're using your HSA as a stealth IRA—maxing contributions, investing, and
                deferring reimbursements for decades. Smart strategy. But when you change
                employers or custodians, your historical data gets trapped or lost.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-0.5">✕</span>
                  <span className="text-slate-700">
                    <strong>Platform Lock-in:</strong> Custodian records disappear when you leave.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-0.5">✕</span>
                  <span className="text-slate-700">
                    <strong>Spreadsheet Fragility:</strong> No receipt storage, no backup, no structure.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 mt-0.5">✕</span>
                  <span className="text-slate-700">
                    <strong>20-Year Risk:</strong> Will your current system still work in 2046?
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-navy rounded-2xl p-8 shadow-2xl text-white">
              <div className="text-sm uppercase tracking-widest text-blue-300 mb-4 font-bold">
                The Stealth IRA Strategy
              </div>
              <div className="text-4xl font-bold mb-4">$147 Billion</div>
              <p className="text-blue-100 leading-relaxed mb-6">
                in HSA assets as of 2024—and growing 10% annually. Sophisticated investors are
                deferring reimbursements for decades. The catch? You need documentation that
                lasts just as long.
              </p>
              <div className="h-1 w-20 bg-gold rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-30 pointer-events-none">
          <div className="absolute top-[10%] left-[-5%] w-96 h-96 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-[10%] right-[-5%] w-96 h-96 bg-yellow-50 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-black text-navy mb-6">Built for the HSA Lifecycle</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Generic expense apps don't understand HSA workflows. Steadfast is purpose-built
              for capturing, organizing, and exporting decades of medical documentation.
            </p>
          </div>

          <div className="space-y-24">
            {/* Feature 1: Capture */}
            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <div className="bg-blue-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                  <svg aria-hidden="true" className="w-8 h-8 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Capture Every Receipt</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  The biggest risk to your strategy is losing documentation. We give you multiple
                  paths to capture every expense the moment it happens.
                </p>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">01</span>
                    <div>
                      <strong className="text-navy block mb-1">Drag &amp; Drop Uploads</strong>
                      <span className="text-sm text-slate-500">
                        PDF, PNG, JPG—we preserve original resolution and metadata.
                        Your receipts will still be legible in 2046.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">02</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-navy">Dedicated Email Inbox</strong>
                        <FeatureBadge intent="core" variant="solid" />
                      </div>
                      <span className="text-sm text-slate-500">
                        Forward digital receipts to{' '}
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-navy text-xs">
                          save@your-vault.steadfasthsa.com
                        </code>
                        . We auto-parse vendor and date.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">03</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-navy">Mobile Capture</strong>
                        <FeatureBadge intent="neutral" variant="outline" />
                      </div>
                      <span className="text-sm text-slate-500">
                        Snap photos at the doctor's office. The PWA works offline and
                        syncs when you're back online.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-lg rotate-1 hover:rotate-0 transition duration-500">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-navy p-3 flex justify-between items-center">
                    <div className="text-xs font-mono text-white opacity-70">New Upload</div>
                    <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="p-6 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 m-4 rounded-lg bg-gray-50 h-48">
                    <svg aria-hidden="true" className="w-10 h-10 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-xs text-slate-400 font-medium">Drop files here or click to upload</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2: Organize */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-lg -rotate-1 hover:rotate-0 transition duration-500 order-2 md:order-1 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-blue-50/50 rounded-3xl pointer-events-none"></div>
                <div className="space-y-4 relative z-10">
                  <div className="bg-navy p-5 rounded-xl shadow-lg border border-blue-900 text-white transform translate-x-2">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-500/20 rounded-lg">
                          <svg aria-hidden="true" className="w-4 h-4 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-xs text-blue-200 font-bold uppercase tracking-widest">
                          Reimbursable Balance
                        </span>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">
                        Tax-Free
                      </span>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">$14,250.00</div>
                    <div className="text-xs text-blue-300">Available to withdraw penalty-free today</div>
                  </div>

                  <div className="bg-white p-4 rounded-xl border-l-4 border-amber-500 shadow-sm transform -translate-x-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-[10px] text-amber-600 uppercase tracking-widest font-bold mb-1 flex items-center gap-1">
                          <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Missing Document
                        </div>
                        <div className="text-navy font-bold text-sm">Add proof of payment</div>
                        <div className="text-slate-500 text-xs mt-1">
                          Dr. Smith visit needs bank/card record
                        </div>
                      </div>
                      <button className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded font-bold hover:bg-amber-100 transition">
                        Fix
                      </button>
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm opacity-70">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">
                        Partial Reimbursement
                      </div>
                      <div className="text-navy font-bold text-xs">$500 / $2,000</div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-gold h-1.5 rounded-full" style={{ width: '25%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-1 md:order-2">
                <div className="bg-blue-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                  <svg aria-hidden="true" className="w-8 h-8 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Organize for the IRS</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Know exactly what you can reimburse and whether your documentation is
                  complete. We track the details so an audit is just a ZIP file away.
                </p>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">01</span>
                    <div>
                      <strong className="text-navy block mb-1">Real-Time Reimbursable Balance</strong>
                      <span className="text-sm text-slate-500">
                        See exactly how much tax-free cash you can access right now.
                        Treat your accumulated receipts as a flexible emergency fund.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">02</span>
                    <div>
                      <strong className="text-navy block mb-1">Documentation Completeness Checks</strong>
                      <span className="text-sm text-slate-500">
                        An invoice alone isn't enough. We flag when you're missing
                        proof of payment or other required documentation.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">03</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-navy">Automatic Mileage Tracking</strong>
                        <FeatureBadge intent="pro" variant="solid" />
                      </div>
                      <span className="text-sm text-slate-500">
                        Don't leave money on the table. We calculate the IRS standard
                        mileage deduction for trips to medical providers.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 3: Export */}
            <div className="grid md:grid-cols-2 gap-16 items-start">
              <div>
                <div className="bg-blue-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                  <svg aria-hidden="true" className="w-8 h-8 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-navy mb-4">Export Everything. Anytime.</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  The entire point is getting your data <em>out</em> in 20 years.
                  We treat the Export button as our most critical feature.
                </p>
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">01</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-navy">One-Click Audit Package</strong>
                        <FeatureBadge intent="core" variant="solid" />
                      </div>
                      <span className="text-sm text-slate-500">
                        Download a ZIP with formatted CSV ledger plus all receipts,
                        organized by date and vendor. IRS-ready in seconds.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">02</span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-navy">BYOS Cloud Sync</strong>
                        <FeatureBadge intent="pro" variant="solid" />
                      </div>
                      <span className="text-sm text-slate-500">
                        Connect your Google Drive or Dropbox. Every receipt automatically
                        syncs to <em>your</em> storage. If Steadfast disappears, you lose nothing.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold-600 font-bold mr-3 mt-1">03</span>
                    <div>
                      <strong className="text-navy block mb-1">Redundant Backups</strong>
                      <span className="text-sm text-slate-500">
                        Geo-redundant, encrypted daily backups. Your data is protected
                        even if you forget to export for a decade.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-lg rotate-1 hover:rotate-0 transition duration-500">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-green-100 p-2 rounded-lg text-green-700 font-bold text-xs uppercase">
                      Export Ready
                    </div>
                    <div className="text-navy font-bold">2026_HSA_Audit_Pack.zip</div>
                  </div>
                  <div className="space-y-2 border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-3">
                      <svg aria-hidden="true" className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <div className="text-xs text-slate-500 font-mono">/receipts/2026-01-28_CVS_42.50.pdf</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg aria-hidden="true" className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <div className="text-xs text-slate-500 font-mono">/receipts/2026-01-15_DrSmith_185.00.jpg</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg aria-hidden="true" className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <div className="text-xs text-slate-500 font-mono">/receipts/2026-01-03_Walgreens_23.99.pdf</div>
                    </div>
                    <div className="flex items-center gap-3 pt-2 border-t border-gray-100 mt-2">
                      <svg aria-hidden="true" className="w-4 h-4 text-navy" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z" />
                      </svg>
                      <div className="text-xs text-navy font-mono font-bold">expenses_2026.csv</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Why Us Section */}
      <section id="trust" className="py-20 bg-blue-50 border-t border-blue-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-navy mb-4">
              We're bootstrapped because this market doesn't need VC economics.
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Most HSA tools are either side features of giant custodians or venture-backed
              startups chasing growth. We're neither.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-blue-100 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-navy mb-3 flex items-center gap-2">
                  <span className="text-gold-600">→</span> Built by HSA optimizers
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We use this strategy ourselves. We got frustrated tracking our own expenses
                  in fragile spreadsheets and decided to build something better.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-navy mb-3 flex items-center gap-2">
                  <span className="text-gold-600">→</span> No investor pressure
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Self-funded means we don't need to 10x next year. We need to be here
                  in 20 years when you take your first reimbursement.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-navy mb-3 flex items-center gap-2">
                  <span className="text-gold-600">→</span> Your data stays yours
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  We don't sell data. We don't show ads. We make money when you pay for
                  the product. That's it. That's the whole business model.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-navy mb-3 flex items-center gap-2">
                  <span className="text-gold-600">→</span> Profitable by design
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Not dependent on future funding rounds. We're building a sustainable
                  business that can serve you for decades.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-blue-200 text-sm font-bold text-navy shadow-sm">
              🛡️ Data Sovereignty First
            </span>
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-blue-200 text-sm font-bold text-navy shadow-sm">
              📈 Boglehead Philosophy
            </span>
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-blue-200 text-sm font-bold text-navy shadow-sm">
              🔒 No Data Selling, Ever
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-navy text-white">
        <div className="max-w-5xl mx-auto px-6 text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-white">Founding Member Pricing</h2>
          <p className="text-blue-200">
            Lock in early supporter rates—yours forever, even when prices go up.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto px-6">
          {/* Free Tier */}
          <div className="bg-white/5 border border-white/10 p-8 rounded-2xl flex flex-col">
            <h3 className="text-xl font-bold mb-2">Free</h3>
            <div className="text-4xl font-bold mb-6">
              $0 <span className="text-sm text-blue-300 font-normal">/ year</span>
            </div>
            <ul className="space-y-4 mb-10 text-blue-100 text-sm flex-grow">
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span> 25 expenses per year
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span> Manual CSV export
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span> Secure cloud storage
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span> Single HSA account
              </li>
            </ul>
            <SignedOut>
              <a href="#waitlist" className="block w-full text-center border border-white/30 py-3 rounded-lg font-bold hover:bg-white/10 transition">
                Join The Waitlist
              </a>
            </SignedOut>
            <SignedIn>
              <Link
                to="/dashboard"
                className="block w-full text-center border border-white/30 py-3 rounded-lg font-bold hover:bg-white/10 transition"
              >
                Go to Dashboard
              </Link>
            </SignedIn>
          </div>

          {/* Core Tier */}
          <div className="bg-white text-navy p-8 rounded-2xl shadow-2xl transform md:scale-105 border-4 border-gold relative flex flex-col">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-4 py-1 rounded-full uppercase tracking-widest">
              Most Popular
            </div>
            <h3 className="text-xl font-bold mb-2">Core</h3>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">$39</span>
                <span className="text-sm text-slate-500 font-normal">/ year</span>
              </div>
              <div className="text-sm text-slate-400 line-through">$49/year regular price</div>
            </div>
            <ul className="space-y-4 mb-10 text-slate-600 text-sm flex-grow font-medium">
              <li className="flex items-center">
                <span className="mr-2 text-gold-600">✓</span>
                <strong>Unlimited</strong>&nbsp;expenses
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold-600">✓</span>
                Dedicated email inbox
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold-600">✓</span>
                Multi-account tracking
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold-600">✓</span>
                Family member tagging
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold-600">✓</span>
                Full ZIP + CSV export
              </li>
            </ul>
            <SignedOut>
              <a href="#waitlist" className="block w-full text-center bg-navy text-white py-4 rounded-lg font-bold hover:opacity-90 transition">
                Join The Waitlist
              </a>
            </SignedOut>
            <SignedIn>
              <Link
                to="/settings"
                className="block w-full text-center bg-navy text-white py-4 rounded-lg font-bold hover:opacity-90 transition"
              >
                Upgrade to Core
              </Link>
            </SignedIn>
          </div>

          {/* Pro Tier */}
          <div className="bg-white/5 border border-white/10 p-8 rounded-2xl flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold">Pro</h3>
              <FeatureBadge intent="neutral" variant="outline" />
            </div>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">$79</span>
                <span className="text-sm text-blue-300 font-normal">/ year</span>
              </div>
              <div className="text-sm text-blue-400 line-through">$99/year regular price</div>
            </div>
            <ul className="space-y-4 mb-10 text-blue-100 text-sm flex-grow">
              <li className="flex items-center">
                <span className="mr-2 text-gold text-lg">★</span>
                <strong>Everything in Core</strong>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span>
                BYOS cloud sync (Drive/Dropbox)
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span>
                Automatic mileage tracking
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span>
                Expense detection (Plaid)
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-gold">✓</span>
                Household access (2 users)
              </li>
            </ul>
            <a href="#waitlist" className="block w-full text-center bg-white/10 py-3 rounded-lg font-bold hover:bg-white/20 transition">
              Join Waitlist — Q3 2026
            </a>
          </div>
        </div>

        {/* Lifetime Deal */}
        <div className="max-w-4xl mx-auto mt-20 px-6">
          <div className="bg-white text-navy p-8 md:p-10 rounded-3xl text-center shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-gold text-navy text-xs font-black px-4 py-2 rounded-bl-xl uppercase tracking-wider">
              Limited Time
            </div>
            <h3 className="text-2xl font-bold mb-4">Founding Member Lifetime Deal</h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              We're offering 500 lifetime deals to early supporters who believe in what we're building.
              Get <strong>all Core features forever</strong> for a one-time payment. No renewals, ever.
            </p>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="text-5xl font-black text-navy">$199</div>
              <div className="text-left">
                <div className="text-sm text-slate-400 line-through">$299 regular price</div>
                <div className="text-sm text-slate-600">one-time payment</div>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <SignedOut>
                <a href="#waitlist" className="bg-navy text-white px-10 py-4 rounded-full text-lg font-bold hover:scale-105 transition duration-300 shadow-lg">
                  Join The Waitlist
                </a>
              </SignedOut>
              <SignedIn>
                <Link
                  to="/settings"
                  className="bg-navy text-white px-10 py-4 rounded-full text-lg font-bold hover:scale-105 transition duration-300 shadow-lg"
                >
                  Claim Lifetime Access
                </Link>
              </SignedIn>
              <p className="mt-4 text-sm text-slate-500">
                <span className="font-bold text-navy">447 of 500</span> spots remaining
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-navy text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-bold text-navy">
                What happens if Steadfast shuts down?
                <span className="text-gold-600 group-open:rotate-180 transition-transform">
                  <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Your data is always exportable—CSV plus all receipt files in one ZIP. If you use
                BYOS (Pro tier), everything automatically syncs to your personal cloud storage.
                We also commit to 12 months notice before any shutdown, with free exports for all
                users during that period. Your documentation is too important to hold hostage.
              </p>
            </details>

            <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-bold text-navy">
                How is my data secured?
                <span className="text-gold-600 group-open:rotate-180 transition-transform">
                  <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                All data is encrypted at rest and in transit. We use geo-redundant cloud storage
                with daily backups. Receipt files are stored separately from your account data.
                We don't have access to your HSA account credentials—we only store documentation
                you upload.
              </p>
            </details>

            <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-bold text-navy">
                Why should I pay when my custodian has free tracking?
                <span className="text-gold-600 group-open:rotate-180 transition-transform">
                  <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Custodian tools lock your data to their platform. When you change jobs or
                custodians—which most people do several times—you lose access to historical records.
                Steadfast is independent of any custodian, tracks multiple accounts in one place,
                and lets you export everything anytime. $39/year is cheap insurance for potentially
                hundreds of thousands in tax-free reimbursements.
              </p>
            </details>

            <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-bold text-navy">
                Do you sell my data?
                <span className="text-gold-600 group-open:rotate-180 transition-transform">
                  <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                No. Never. We make money when you pay for the product. That's it. We don't sell
                data, we don't show ads, we don't do "anonymous" analytics partnerships. Your
                medical expense data is yours alone.
              </p>
            </details>

            <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer">
              <summary className="flex justify-between items-center font-bold text-navy">
                What if I want a refund?
                <span className="text-gold-600 group-open:rotate-180 transition-transform">
                  <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">
                30-day money-back guarantee on all plans, no questions asked. For lifetime deals,
                we offer a 60-day refund window. If Steadfast isn't working for you, we don't
                want your money.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-navy mb-4">
            Ready to stop worrying about your HSA documentation?
          </h2>
          <p className="text-slate-600 mb-8">
            Start free. Upgrade when you're ready. Export anytime.
          </p>
          <SignedOut>
            <a href="#waitlist" className="inline-block bg-navy text-white px-8 py-4 rounded-lg text-lg font-bold shadow-lg hover:translate-y-[-2px] transition">
              Join The Waitlist
            </a>
          </SignedOut>
          <SignedIn>
            <Link
              to="/dashboard"
              className="inline-block bg-navy text-white px-8 py-4 rounded-lg text-lg font-bold shadow-lg hover:translate-y-[-2px] transition"
            >
              Go to Dashboard
            </Link>
          </SignedIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
            <div className="mb-6 md:mb-0">
              <span className="font-bold text-navy">STEADFAST HSA</span> © {new Date().getFullYear()}. Built for the long term.
            </div>
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <a href="/security" className="hover:text-navy transition">Security</a>
              <Link to="/privacy" className="hover:text-navy transition">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-navy transition">Terms of Service</Link>
              <Link to="/security" className="hover:text-navy transition">Security</Link>

            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
